// src/components/ArticlePage.jsx

import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import SEO, { createBreadcrumbData } from "./SEO";

const articleMeta = {
  "holiday-cards-complete-guide": {
    title: "Why I Still Send Holiday Cards (And Why You Should Too)",
    author: "Emma Mitchell",
    date: "December 2024",
    category: "Holiday Cards",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1482517967863-00e15c9b44be?w=1200&q=80"
  },
  "ai-greeting-cards-revolution": {
    title: "I Let AI Design My Holiday Cards and My Mom Thought I Hired an Artist",
    author: "Emma Mitchell",
    date: "December 2024",
    category: "AI & Technology",
    readTime: "10 min read",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80"
  },
  "christmas-card-photo-tips": {
    title: "How to Take Christmas Card Photos That Don't Make Your Family Look Unhinged",
    author: "Emma Mitchell",
    date: "December 2024",
    category: "Photography Tips",
    readTime: "12 min read",
    image: "https://images.unsplash.com/photo-1511895426328-dc8714191300?w=1200&q=80"
  },
  "holiday-card-message-ideas": {
    title: "100+ Holiday Card Messages for Every Situation",
    author: "Editorial Team",
    date: "December 2024",
    category: "Writing Tips",
    readTime: "15 min read",
    image: "https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=1200&q=80"
  },
  "eco-friendly-holiday-cards": {
    title: "Sustainable Holiday Cards: How to Send Greetings Without the Guilt",
    author: "Editorial Team",
    date: "December 2024",
    category: "Sustainability",
    readTime: "9 min read",
    image: "https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=1200&q=80"
  },
  "diy-card-ideas": {
    title: "10 DIY Card Ideas That Look Store-Bought (But Aren't)",
    author: "Emma Mitchell",
    date: "December 2024",
    category: "DIY Ideas",
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=80"
  },
  "last-minute-card-guide": {
    title: "The Procrastinator's Guide to Last-Minute Holiday Cards",
    author: "Emma Mitchell",
    date: "December 2024",
    category: "Holiday Cards",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1200&q=80"
  },
  "valentines-day-card-messages": {
    title: "75+ Valentine's Day Card Messages That Actually Sound Like You Wrote Them",
    author: "Emma Mitchell",
    date: "January 2026",
    category: "Valentine's Day",
    readTime: "10 min read",
    image: "https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=1200&q=80"
  },
  "creative-valentines-cards-ideas": {
    title: "15 Creative Valentine's Day Card Ideas That Go Beyond Red Hearts",
    author: "Emma Mitchell",
    date: "January 2026",
    category: "Valentine's Day",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1581404917879-53e19259fdda?w=1200&q=80"
  },
  "valentines-day-photo-cards": {
    title: "Valentine's Day Photo Card Tips: From Snapshot to Stunning",
    author: "Emma Mitchell",
    date: "January 2026",
    category: "Valentine's Day",
    readTime: "12 min read",
    image: "https://images.unsplash.com/photo-1474552226712-ac0f0961a954?w=1200&q=80"
  }
};

export default function ArticlePage() {
  const { articleId } = useParams();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const meta = articleMeta[articleId];

  // Custom link component that uses React Router for internal links
  const CustomLink = ({ href, children, ...props }) => {
    const isInternal = href && (href.startsWith('/') || href.startsWith('#'));
    
    if (isInternal && !href.startsWith('#')) {
      return (
        <Link 
          to={href} 
          className="text-purple-600 hover:text-purple-800 underline font-medium"
          {...props}
        >
          {children}
        </Link>
      );
    }
    
    // External links or anchor links
    return (
      <a 
        href={href} 
        className="text-purple-600 hover:text-purple-800 underline font-medium"
        target={isInternal ? undefined : "_blank"}
        rel={isInternal ? undefined : "noopener noreferrer"}
        {...props}
      >
        {children}
      </a>
    );
  };

  // Scroll to top when article changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleId]);

  useEffect(() => {
    // Fetch the markdown file
    fetch(`/blog/${articleId}.md`)
      .then((res) => {
        if (!res.ok) throw new Error("Article not found");
        return res.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load article:", err);
        setContent("# Article Not Found\n\nSorry, we couldn't find this article.");
        setLoading(false);
      });
  }, [articleId]);

  if (!meta) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <Link to="/articles" className="text-purple-600 hover:underline">
            ← Back to Articles
          </Link>
        </div>
      </div>
    );
  }

  // Create structured data for the article
  const articleStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: meta.title,
    image: meta.image,
    author: {
      '@type': 'Person',
      name: meta.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'DreamableAI',
      logo: {
        '@type': 'ImageObject',
        url: 'https://dreamableai.com/logo512.png',
      },
    },
    datePublished: '2024-12-16',
    dateModified: '2024-12-16',
    description: `${meta.title} - ${meta.category} article on DreamableAI`,
  };

  const breadcrumbData = createBreadcrumbData([
    { name: 'Home', path: '/' },
    { name: 'Articles', path: '/articles' },
    { name: meta.title, path: `/articles/${articleId}` },
  ]);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={meta.title}
        description={`${meta.title} - Read our ${meta.category.toLowerCase()} article about creating amazing greeting cards.`}
        path={`/articles/${articleId}`}
        image={meta.image}
        type="article"
        structuredData={[articleStructuredData, breadcrumbData]}
      />
      {/* Hero Image */}
      <div className="relative h-64 md:h-96 overflow-hidden">
        <img 
          src={meta.image} 
          alt={meta.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-4xl mx-auto">
            <span className="inline-block px-3 py-1 bg-purple-600 text-white text-sm font-medium rounded-full mb-4">
              {meta.category}
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {meta.title}
            </h1>
            <div className="flex items-center text-white/90 text-sm md:text-base">
              <span className="font-medium">{meta.author}</span>
              <span className="mx-2">•</span>
              <span>{meta.date}</span>
              <span className="mx-2">•</span>
              <span>{meta.readTime}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Back Link */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <Link 
          to="/articles" 
          className="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Articles
        </Link>
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600"></div>
          </div>
        ) : (
          <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-purple-600 prose-strong:text-gray-900 prose-img:rounded-xl prose-img:shadow-lg">
            <ReactMarkdown
              components={{
                img: ({ node, ...props }) => (
                  <figure className="my-8">
                    <img {...props} className="w-full rounded-xl shadow-lg" alt={props.alt || ""} />
                    {props.alt && (
                      <figcaption className="text-center text-sm text-gray-500 mt-3 italic">
                        {props.alt}
                      </figcaption>
                    )}
                  </figure>
                ),
                // eslint-disable-next-line jsx-a11y/heading-has-content
                h1: ({ node, children, ...props }) => (
                  <h1 className="text-3xl md:text-4xl font-bold mt-8 mb-4" {...props}>{children}</h1>
                ),
                // eslint-disable-next-line jsx-a11y/heading-has-content
                h2: ({ node, children, ...props }) => (
                  <h2 className="text-2xl md:text-3xl font-bold mt-10 mb-4 pb-2 border-b border-gray-200" {...props}>{children}</h2>
                ),
                h3: ({ node, children, ...props }) => (
                  <h3 className="text-xl md:text-2xl font-bold mt-8 mb-3" {...props}>{children}</h3>
                ),
                blockquote: ({ node, ...props }) => (
                  <blockquote className="border-l-4 border-purple-500 pl-6 py-2 my-6 bg-purple-50 rounded-r-lg italic text-gray-700" {...props} />
                ),
                table: ({ node, ...props }) => (
                  <div className="overflow-x-auto my-6">
                    <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden" {...props} />
                  </div>
                ),
                th: ({ node, ...props }) => (
                  <th className="bg-purple-100 px-4 py-3 text-left font-semibold text-gray-900 border-b" {...props} />
                ),
                td: ({ node, ...props }) => (
                  <td className="px-4 py-3 border-b border-gray-100" {...props} />
                ),
                ul: ({ node, ...props }) => (
                  <ul className="list-disc list-inside space-y-2 my-4" {...props} />
                ),
                ol: ({ node, ...props }) => (
                  <ol className="list-decimal list-inside space-y-2 my-4" {...props} />
                ),
                hr: ({ node, ...props }) => (
                  <hr className="my-12 border-gray-200" {...props} />
                ),
                a: CustomLink,
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
      </article>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-purple-600 to-indigo-600 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Create Your Own Cards?
          </h2>
          <p className="text-lg text-purple-100 mb-6">
            Transform your photos into stunning greeting cards with our AI-powered tools
          </p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 bg-white text-purple-600 font-bold rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
          >
            Start Creating
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </section>

      {/* Related Articles */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">More Articles</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {Object.entries(articleMeta)
            .filter(([id]) => id !== articleId)
            .slice(0, 2)
            .map(([id, article]) => (
              <Link
                key={id}
                to={`/articles/${id}`}
                className="group flex gap-4 p-4 bg-gray-50 rounded-xl hover:bg-purple-50 transition-colors"
              >
                <img 
                  src={article.image} 
                  alt={article.title}
                  className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                />
                <div>
                  <h3 className="font-bold text-gray-900 group-hover:text-purple-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">{article.author}</p>
                </div>
              </Link>
            ))}
        </div>
      </section>
    </div>
  );
}


